import { useState, useEffect } from 'react';

/* ─── Utilidades de color ────────────────────────────────────────────────── */
const readVar = (name, fallback) => {
  if (typeof window === 'undefined') return fallback;
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim() || fallback;
};

// "r g b" -> [r, g, b]
const parseRgb = (str, fb) => {
  const p = (str || '').split(/[\s,]+/).map(Number).filter(n => !Number.isNaN(n));
  return p.length >= 3 ? p.slice(0, 3) : fb;
};
const mix = (a, b, t) => a.map((v, i) => Math.round(v + (b[i] - v) * t));
const css = (c, a = 1) => `rgb(${c[0]} ${c[1]} ${c[2]} / ${a})`;   // para CSS
const svg = (c) => `rgb(${c[0]},${c[1]},${c[2]})`;                  // para atributos SVG
const luminance = ([r, g, b]) => (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;

const WHITE = [255, 255, 255];
const BLACK = [0, 0, 0];

const readBrand = () => ({
  primary:   parseRgb(readVar('--brand-primary-rgb',   ''), [0, 245, 255]),
  secondary: parseRgb(readVar('--brand-secondary-rgb', ''), [168, 85, 247]),
  accent:    parseRgb(readVar('--brand-accent-rgb',    ''), [204, 255, 0]),
  bg:        parseRgb(readVar('--brand-bg-rgb',        ''), [5, 6, 18]),
  text:      parseRgb(readVar('--brand-text-rgb',      ''), [224, 247, 255]),
});

function useBrandColors() {
  const [c, setC] = useState(readBrand);
  useEffect(() => {
    const update = () => setC(readBrand());
    const obs = new MutationObserver(update);
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ['style', 'data-theme'] });
    update();
    return () => obs.disconnect();
  }, []);
  return c;
}

/* ─── Widget ─────────────────────────────────────────────────────────────── */
const CircleWidget = ({ items = [], users = [], currentUser = null, completionRate = 0 }) => {
  const [hovered, setHovered]   = useState(null);
  const [animated, setAnimated] = useState(false);
  const [tick, setTick]         = useState(0);
  const { primary, secondary, accent, bg, text } = useBrandColors();

  const isSuperAdmin = currentUser?.role === 'superadmin';
  const isLight      = luminance(bg) > 0.55;

  // Colores derivados del fondo elegido por el superadmin
  const bgLight  = mix(bg, WHITE, isLight ? 0.35 : 0.14);
  const bgDark   = mix(bg, BLACK, isLight ? 0.22 : 0.55);
  const textCol  = css(text);
  const centerC  = isSuperAdmin ? primary : secondary;
  const palette  = [primary, secondary, accent, primary, secondary, accent];

  const rawDataList = items.length > 0 ? items : users;
  const dataList = rawDataList.filter(i => !(i?.name?.toLowerCase() || '').includes('eliminado'));

  useEffect(() => {
    const t = setTimeout(() => setAnimated(true), 150);
    return () => clearTimeout(t);
  }, []);
  useEffect(() => {
    if (!animated) return;
    const id = setInterval(() => setTick(n => n + 1), 50);
    return () => clearInterval(id);
  }, [animated]);

  const W = 600, H = 600, cx = W / 2, cy = H / 2;
  const rot1 =  (tick * 1.2) % 360;
  const rot2 = -(tick * 0.8) % 360;
  const rot3 =  (tick * 0.5) % 360;

  const display = dataList.slice(0, 12);
  const o1 = display.slice(0, 4);
  const o2 = display.slice(4, 9);
  const o3 = display.slice(9, 12);

  const pos = (count, radius, rot) =>
    Array.from({ length: count }, (_, i) => {
      const a = (i / count) * Math.PI * 2 + (rot * Math.PI / 180);
      return { x: cx + radius * Math.cos(a), y: cy + radius * Math.sin(a) };
    });

  const p1 = pos(o1.length, 140, rot1);
  const p2 = pos(o2.length, 220, rot2);
  const p3 = pos(o3.length, 290, rot3);

  const allNodes = [
    ...o1.map((u, i) => ({ item: u, ...p1[i], size: 28 })),
    ...o2.map((u, i) => ({ item: u, ...p2[i], size: 26 })),
    ...o3.map((u, i) => ({ item: u, ...p3[i], size: 24 })),
  ];

  // Arco de progreso
  const arcR   = 62;
  const pct    = isSuperAdmin ? 100 : completionRate;
  const aAngle = (pct / 100) * Math.PI * 2;
  const arcX   = cx + arcR * Math.cos(-Math.PI / 2 + aAngle);
  const arcY   = cy + arcR * Math.sin(-Math.PI / 2 + aAngle);
  const largeArc = aAngle > Math.PI ? 1 : 0;

  const renderParticles = () =>
    Array.from({ length: 22 }, (_, p) => {
      const angle = (p / 22) * Math.PI * 2 + (rot1 * Math.PI / 180) * (p % 2 === 0 ? 0.45 : -0.28);
      const r = 88 + (p % 6) * 35;
      const op = 0.35 + 0.35 * Math.sin(tick * 0.055 + p);
      return (
        <circle key={p} cx={cx + r * Math.cos(angle)} cy={cy + r * Math.sin(angle)}
          r={1.7} fill={svg(palette[p % palette.length])} opacity={op} />
      );
    });

  if (dataList.length === 0 && !currentUser) {
    return (
      <div style={{
        height: 120, display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: css(bg, 0.6), borderRadius: 16, border: `1px dashed ${css(primary, 0.3)}`,
      }}>
        <span style={{ fontSize: 12, color: css(text, 0.5), fontWeight: 800,
          letterSpacing: '0.1em', textTransform: 'uppercase', fontFamily: 'DM Sans, sans-serif' }}>
          Sin nodos en la red
        </span>
      </div>
    );
  }

  /* ─── Fondo con volumen 3D ─── */
  const containerStyle = {
    position: 'relative', width: '100%', aspectRatio: '1',
    maxWidth: 400, margin: '0 auto', borderRadius: 28, overflow: 'hidden',
    background: `
      radial-gradient(circle at 30% 18%, ${css(bgLight)} 0%, ${css(bg)} 48%, ${css(bgDark)} 100%)`,
    border: `1px solid ${css(secondary, 0.35)}`,
    boxShadow: `
      0 28px 50px -14px rgb(0 0 0 / 0.55),
      0 0 38px ${css(secondary, 0.22)},
      inset 0 2px 1px rgb(255 255 255 / ${isLight ? 0.6 : 0.22}),
      inset 0 -22px 40px rgb(0 0 0 / ${isLight ? 0.18 : 0.4}),
      inset 0 0 70px ${css(primary, 0.14)}`,
  };

  return (
    <div style={containerStyle}>
      {/* Suelo en perspectiva */}
      <div style={{
        position: 'absolute', left: '-30%', right: '-30%', bottom: '-18%', height: '75%',
        transform: 'perspective(380px) rotateX(64deg)', transformOrigin: 'center bottom',
        backgroundImage: `
          repeating-linear-gradient(0deg,  transparent 0 34px, ${css(primary, 0.35)} 34px 35px),
          repeating-linear-gradient(90deg, transparent 0 34px, ${css(primary, 0.35)} 34px 35px)`,
        WebkitMaskImage: 'linear-gradient(to top, #000 15%, transparent 85%)',
        maskImage: 'linear-gradient(to top, #000 15%, transparent 85%)',
        pointerEvents: 'none',
      }} />

      {/* Halo de profundidad detrás del centro */}
      <div style={{
        position: 'absolute', inset: 0, pointerEvents: 'none',
        background: `radial-gradient(circle at 50% 50%, ${css(centerC, 0.28)} 0%, transparent 52%)`,
      }} />

      {/* Brillo de cristal arriba */}
      <div style={{
        position: 'absolute', top: '-12%', left: '-8%', width: '90%', height: '48%',
        background: `radial-gradient(ellipse at 35% 30%, rgb(255 255 255 / ${isLight ? 0.55 : 0.16}) 0%, transparent 70%)`,
        borderRadius: '50%', pointerEvents: 'none',
      }} />

      <svg width="100%" height="100%" viewBox={`0 0 ${W} ${H}`}
        style={{ display: 'block', position: 'absolute', top: 0, left: 0 }}>
        <defs>
          <filter id="cw-glow" x="-60%" y="-60%" width="220%" height="220%">
            <feGaussianBlur stdDeviation="5" result="b" />
            <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
          </filter>
          <filter id="cw-soft" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="4" />
          </filter>

          {/* Esferas 3D: luz arriba-izquierda, sombra abajo-derecha */}
          {palette.slice(0, 3).map((c, i) => (
            <radialGradient key={`sp${i}`} id={`cw-sp${i}`} cx="32%" cy="28%" r="80%">
              <stop offset="0%"   stopColor={svg(mix(c, WHITE, 0.65))} />
              <stop offset="40%"  stopColor={svg(c)} />
              <stop offset="100%" stopColor={svg(mix(c, BLACK, 0.6))} />
            </radialGradient>
          ))}
          <radialGradient id="cw-center" cx="32%" cy="28%" r="80%">
            <stop offset="0%"   stopColor={svg(mix(centerC, WHITE, 0.7))} />
            <stop offset="45%"  stopColor={svg(centerC)} />
            <stop offset="100%" stopColor={svg(mix(centerC, BLACK, 0.6))} />
          </radialGradient>
          <radialGradient id="cw-shine" cx="30%" cy="25%" r="55%">
            <stop offset="0%"   stopColor="#fff" stopOpacity="0.55" />
            <stop offset="100%" stopColor="#fff" stopOpacity="0" />
          </radialGradient>

          {allNodes.map((n, i) => (
            <clipPath key={`cp${i}`} id={`cw-cp${i}`}><circle cx={n.x} cy={n.y} r={n.size - 1} /></clipPath>
          ))}
          <clipPath id="cw-cpc"><circle cx={cx} cy={cy} r={43} /></clipPath>
        </defs>

        {/* Órbitas */}
        {[140, 220, 290].map((r, i) => (
          <circle key={i} cx={cx} cy={cy} r={r} fill="none"
            stroke={css(primary, 0.22)} strokeWidth="1" strokeDasharray={i === 0 ? undefined : '3,8'} />
        ))}

        {animated && renderParticles()}

        {/* Líneas de conexión */}
        {animated && allNodes.map((node, i) => {
          const isHov = hovered === i;
          return (
            <line key={`l${i}`} x1={cx} y1={cy} x2={node.x} y2={node.y}
              stroke={svg(palette[i % palette.length])}
              strokeWidth={isHov ? 2 : 0.8} opacity={isHov ? 0.9 : 0.25}
              style={{ transition: 'opacity 0.3s' }} />
          );
        })}

        {/* Nodos */}
        {allNodes.map((node, i) => {
          const ci  = i % 3;
          const col = palette[ci];
          const r   = node.size;
          const isHov = hovered === i;
          const name  = node.item?.name || '?';
          return (
            <g key={`n${i}`}
              style={{ opacity: animated ? 1 : 0, transition: 'opacity 0.6s', cursor: 'pointer' }}
              onMouseEnter={() => setHovered(i)} onMouseLeave={() => setHovered(null)}>
              {/* sombra proyectada en el "suelo" */}
              <ellipse cx={node.x + 3} cy={node.y + r + 7} rx={r * 0.85} ry={r * 0.22}
                fill="#000" opacity={isLight ? 0.25 : 0.45} filter="url(#cw-soft)" />
              {/* aro de glow */}
              <circle cx={node.x} cy={node.y} r={r + (isHov ? 10 : 5)} fill="none"
                stroke={svg(col)} strokeWidth="1.5" opacity={isHov ? 0.9 : 0.3}
                filter="url(#cw-glow)" style={{ transition: 'all 0.25s' }} />
              {/* esfera */}
              <circle cx={node.x} cy={node.y} r={r} fill={`url(#cw-sp${ci})`}
                stroke={svg(mix(col, WHITE, 0.3))} strokeWidth="1.5" />
              {node.item?.avatar_url ? (
                <image href={node.item.avatar_url} x={node.x - r + 1} y={node.y - r + 1}
                  width={(r - 1) * 2} height={(r - 1) * 2}
                  clipPath={`url(#cw-cp${i})`} preserveAspectRatio="xMidYMid slice" />
              ) : (
                <text x={node.x} y={node.y} textAnchor="middle" dominantBaseline="central"
                  fill="#fff" fontSize={r * 0.55} fontWeight="900" fontFamily="DM Sans, sans-serif"
                  style={{ paintOrder: 'stroke', stroke: 'rgb(0 0 0 / 0.35)', strokeWidth: 2 }}>
                  {name.split(' ').slice(0, 2).map(w => w[0]?.toUpperCase() || '').join('')}
                </text>
              )}
              {/* brillo especular encima */}
              <circle cx={node.x} cy={node.y} r={r} fill="url(#cw-shine)" pointerEvents="none" />

              {isHov && (
                <g>
                  <rect x={node.x - 62} y={node.y - r - 32} width="124" height="22" rx="6"
                    fill={css(bg, 0.95)} stroke={svg(col)} strokeWidth="1" />
                  <text x={node.x} y={node.y - r - 21} textAnchor="middle" dominantBaseline="central"
                    fill={textCol} fontSize="10" fontWeight="800"
                    fontFamily="DM Sans, sans-serif" letterSpacing="0.05em">
                    {name.length > 17 ? name.slice(0, 16) + '…' : name}
                  </text>
                </g>
              )}
            </g>
          );
        })}

        {/* Nodo central */}
        {currentUser && (
          <g style={{ opacity: animated ? 1 : 0, transition: 'opacity 0.8s 0.2s' }}>
            <circle cx={cx} cy={cy} r="76" fill="none" stroke={css(centerC, 0.25)} strokeWidth="2"
              style={{ animation: 'cwPulse 2.5s ease-in-out infinite' }} />
            <circle cx={cx} cy={cy} r="65" fill="none" stroke={css(centerC, 0.4)} strokeWidth="1"
              style={{ animation: 'cwPulse 2.5s ease-in-out infinite 0.65s' }} />
            <circle cx={cx} cy={cy} r={arcR} fill="none" stroke={css(text, 0.12)} strokeWidth="4" />

            {(isSuperAdmin || (pct > 0 && pct < 100)) && (
              <path d={`M ${cx} ${cy - arcR} A ${arcR} ${arcR} 0 ${largeArc} 1 ${arcX} ${arcY}`}
                fill="none" stroke={svg(isSuperAdmin ? primary : accent)}
                strokeWidth="4" strokeLinecap="round" opacity="0.95" filter="url(#cw-glow)" />
            )}
            {pct === 100 && !isSuperAdmin && (
              <circle cx={cx} cy={cy} r={arcR} fill="none" stroke={svg(accent)}
                strokeWidth="4" opacity="0.95" filter="url(#cw-glow)" />
            )}

            {/* sombra del orbe central */}
            <ellipse cx={cx + 4} cy={cy + 58} rx="38" ry="9" fill="#000"
              opacity={isLight ? 0.25 : 0.5} filter="url(#cw-soft)" />
            <circle cx={cx} cy={cy} r="45" fill="url(#cw-center)"
              stroke={svg(mix(centerC, WHITE, 0.35))} strokeWidth="3" filter="url(#cw-glow)" />
            {currentUser?.avatar_url ? (
              <image href={currentUser.avatar_url} x={cx - 43} y={cy - 43} width="86" height="86"
                clipPath="url(#cw-cpc)" preserveAspectRatio="xMidYMid slice" />
            ) : (
              <text x={cx} y={cy} textAnchor="middle" dominantBaseline="central" fill="#fff"
                fontSize="26" fontWeight="900" fontFamily="DM Sans, sans-serif"
                style={{ paintOrder: 'stroke', stroke: 'rgb(0 0 0 / 0.35)', strokeWidth: 3 }}>
                {isSuperAdmin ? 'SA'
                  : (currentUser?.name || '?').split(' ').slice(0, 2).map(w => w[0]?.toUpperCase() || '').join('')}
              </text>
            )}
            <circle cx={cx} cy={cy} r="45" fill="url(#cw-shine)" pointerEvents="none" />

            <text x={cx} y={cy + 78} textAnchor="middle" fill={svg(centerC)} fontSize="11"
              fontWeight="900" fontFamily="DM Sans, sans-serif" letterSpacing="0.2em">
              {isSuperAdmin ? 'RED GLOBAL' : 'TÚ'}
            </text>

            {!isSuperAdmin && pct > 0 && (
              <g>
                <rect x={cx - 26} y={cy - 98} width="52" height="22" rx="6"
                  fill={css(bg, 0.92)} stroke={svg(accent)} strokeWidth="1.5" />
                <text x={cx} y={cy - 87} textAnchor="middle" dominantBaseline="central"
                  fill={svg(accent)} fontSize="11" fontWeight="900" fontFamily="DM Sans, sans-serif">
                  {Math.round(pct)}%
                </text>
              </g>
            )}
          </g>
        )}
      </svg>

      <style>{`
        @keyframes cwPulse { 0%,100% { opacity: 0.07; } 50% { opacity: 0.55; } }
      `}</style>
    </div>
  );
};

export default CircleWidget;